// zh.ts — Simplified Chinese catalog. `satisfies Catalog` is the whole safety net: a missing
// key, a misspelled key, or a function with the wrong params is a `check-types` failure.
//
// Translated per docs/superpowers/specs/locale-translator-contract.md. Register: second person
// "你" throughout (informal, modern Chinese SaaS voice) — see the translator report for term
// choices (widget → 聊天插件, lead → 线索, dashboard → 控制台, crawler/crawl → 爬虫/抓取,
// workspace → 工作区, plan → 套餐, seat → 席位). "Playground" kept as an English feature/nav
// name, same convention many Chinese dev tools use for this exact word.
import { type Catalog } from './en';

export const zh = {
  common: {
    close: '关闭',
  },
  status: {
    pending: '待处理',
    crawling: '抓取中',
    indexing: '索引中',
    ready: '就绪',
    error: '错误',
    paused: '已暂停',
    hint: '提示',
    deleting: '删除中',
  },
  time: {
    never: '从未',
    justNow: '刚刚',
    inAMoment: '马上',
    secondsAgo: ({ n }: { n: number }) => `${n}秒前`,
    inSeconds: ({ n }: { n: number }) => `${n}秒后`,
    minutesAgo: ({ n }: { n: number }) => `${n}分钟前`,
    inMinutes: ({ n }: { n: number }) => `${n}分钟后`,
    hoursAgo: ({ n }: { n: number }) => `${n}小时前`,
    inHours: ({ n }: { n: number }) => `${n}小时后`,
    daysAgo: ({ n }: { n: number }) => `${n}天前`,
    inDays: ({ n }: { n: number }) => `${n}天后`,
    monthsAgo: ({ n }: { n: number }) => `${n}个月前`,
    inMonths: ({ n }: { n: number }) => `${n}个月后`,
    yearsAgo: ({ n }: { n: number }) => `${n}年前`,
    inYears: ({ n }: { n: number }) => `${n}年后`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) => `正在以 ${email} 的身份查看——你正在模拟这位用户。`,
    stopImpersonating: '停止',

    platformAdmin: '平台管理',
    adminTabWorkspaces: '工作区',
    adminTabBots: '机器人',
    adminTabJobs: '任务',
    adminTabPosts: '文章',
    adminTabFeedback: '反馈',
    adminTabPlayground: 'Playground',

    botsLoadError: '无法加载你的机器人。',
    botsLoadErrorInline: '无法加载你的机器人',
    reloadToRetry: '刷新页面重试。',

    navOverview: '概览',
    navUnanswered: '未解答',
    navConversations: '对话',
    navLeads: '线索',
    navSources: '来源',
    navPlayground: 'Playground',
    navAppearance: '外观',
    navSettings: '设置',
    groupNeedsYou: '待处理',
    groupKnowledge: '知识库',
    groupSetup: '设置',

    closeMenu: '关闭菜单',
    navigationLabel: '导航',
    openNavigation: '打开导航',
    openAccountMenu: '打开账户菜单',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name},切换机器人`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `你的机器人${cap !== null ? ` · 已用 ${n}/${cap}` : ''}`,
    allBots: '所有机器人',
    newBot: '新建机器人',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `新建机器人——已用 ${n}/${cap}`,

    pagesIndexed: '已索引页面',

    feedbackTrigger: '反馈',
    feedbackTypeGroupLabel: '反馈类型',
    feedbackTypeBug: '问题反馈',
    feedbackTypeFeature: '功能建议',
    feedbackTypeQuestion: '疑问',
    feedbackPlaceholder: '发生了什么,或者怎样能让 Embent 对你更好用?',
    feedbackSent: '已发送——我们会认真看每一条。',
    feedbackSendError: '无法发送反馈。',
    feedbackSending: '发送中…',
    feedbackSend: '发送',
    feedbackDialogTitle: '发送反馈',
    feedbackDialogDescription: '问题反馈、功能建议或疑问——都会直接送到我们团队手上。',

    workspaceSettings: '工作区设置',
    members: '成员',
    billingUsage: '账单与用量',
    switchWorkspace: '切换工作区',
    accountLabelPrefix: '账户——',
    account: '账户',
    signOut: '退出登录',
    themePrefix: '主题:',
    languagePrefix: '语言:',
    nextPrefix: '下一个:',
    themeSystem: '跟随系统',
    themeDark: '深色',
    themeLight: '浅色',
    roleOwner: '所有者',
    roleAdmin: '管理员',
    roleMember: '成员',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · 你是${role}` : `你是${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name},工作区与账户菜单`,

    usageWarning: ({ pct }: { pct: number }) => `你已使用本月消息额度的 ${pct}%——升级套餐,让每一次对话都畅通无阻。`,
    dismiss: '关闭',

    renewsOn: ({ date }: { date: string }) => `${date} 续期`,
    resetsOn: ({ date }: { date: string }) => `${date} 重置`,
    answersPaused: '新回答已暂停——已超出额度 110%',
    messagesLeft: ({ n }: { n: string }) => `本月还剩 ${n} 条消息`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} 仍会显示"Powered by Embent"标识`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} 可以关闭"Powered by Embent"标识`,
    upgradeCta: ({ price }: { price: number }) => `升级——每月 $${price}`,
    resumeAnswersCta: ({ price }: { price: number }) => `恢复回答——每月 $${price}`,
    resumeAnswersBenefit: '升级后立即恢复回答',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} 新增 ${messages} 条消息、${bots} 个机器人和 ${seats} 个席位`,
  },
  bots: {
    noSources: '暂无来源',
    allSourcesReady: ({ n }: { n: number }) => `${n} · 全部就绪`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: '已暂停',
    sourcesRemoving: ({ n }: { n: number }) => `${n} 个来源 · 正在移除`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} 个来源 · 同步出错`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} 个来源 · 同步中`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} 条消息 · ${unanswered} 条未解答`,
    botsCount: ({ n }: { n: number }) => `${n} 个机器人`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `已用 ${n}/${cap}`,

    colBot: '机器人',
    colStatus: '状态',
    colMessages14d: '消息 · 14天',
    colUnanswered: '未解答',
    colSources: '来源',
    pageTitle: '机器人',
    emptyTitle: '创建你的第一个机器人',
    emptyDescription: '把它指向你的网站或文档——它会抓取内容并开始回答访客问题。',
    planIncludesBots: ({ cap }: { cap: number }) => `你的套餐包含 ${cap} 个机器人`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name}(每月 $${priceUsd})最多支持 ${maxBots} 个机器人——每个网站一个,各有自己的知识库和外观。`,
    comparePlansLink: '比较套餐 →',
    newBotDialogTitle: '新建机器人',
    botNameFieldLabel: '名称',
    botNamePlaceholder: 'Acme 客服机器人',
    creatingCta: '创建中…',
    createCta: '创建',
  },
  auth: {
    emailLabel: '邮箱',
    passwordLabel: '密码',
    genericFormError: '出了点问题,请重试。',
    continueWithGoogle: '使用 Google 继续',
    backToSignIn: '返回登录',

    signInTitle: '登录',
    signInSubtitle: '欢迎回到 Embent。',
    passwordJustResetNotice: '密码已更新——用新密码登录。',
    forgotPasswordLink: '忘记密码?',
    unverifiedResendNotice: '请查看邮箱——我们已重新发送验证邮件。',
    signingIn: '登录中…',
    signInCta: '登录',
    noAccountPrompt: '还没有账户?',
    signUpLink: '注册',

    signUpTitle: '创建账户',
    signUpSubtitle: '几分钟内就能开始回答访客问题。',
    nameLabel: '姓名',
    creatingAccount: '创建账户中…',
    createAccountCta: '创建账户',
    haveAccountPrompt: '已经有账户了?',
    signInLink: '登录',

    checkEmailTitle: '查看你的邮箱',
    checkEmailSubtitle: '我们已发送验证链接,通常一分钟内送达。',
    checkEmailBody: '点击邮件中的链接以激活账户。',

    verifyFailedTitle: '验证失败',
    verifiedTitle: '邮箱已验证',
    verifyFailedBody: '这个验证链接无效或已过期。',
    verifiedBody: '一切就绪——正在带你进入控制台…',

    requestResetTitle: '重置密码',
    requestResetSubtitlePending: '输入你的邮箱,我们会发送重置链接。',
    requestResetSubtitleSent: '如果这个邮箱对应一个 Embent 账户,我们已发送重置链接。',
    requestResetBodySent: '请查看你的邮箱。链接 1 小时后失效。',
    sendingResetLink: '发送中…',
    sendResetLinkCta: '发送重置链接',

    resetLinkInvalidTitle: '重置链接无效',
    resetLinkInvalidBody: '这个密码重置链接缺少令牌,或者已经被使用过。',
    requestNewLinkCta: '申请新链接',
    chooseNewPasswordTitle: '设置新密码',
    chooseNewPasswordSubtitle: '换一个你之前没用过的密码。',
    newPasswordLabel: '新密码',
    resetLinkExpiredFallback: '这个重置链接无效或已过期。',
    savingPassword: '保存中…',
    saveNewPasswordCta: '保存新密码',

    inviteNotValidTitle: '邀请无效',
    goToEmbentLink: '前往 Embent',
    inviteInvalidFallback: '这个邀请已经失效。',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `你被邀请加入 ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) => `已发送到 ${email}——用这个邮箱登录或创建账户即可接受邀请。`,
    inviteSignInCta: '登录',
    inviteSignUpCta: '创建账户',
    wrongAccountTitle: '账户不匹配',
    signOutCta: '退出登录',
    inviteAcceptFallback: '接受这个邀请时出了点问题。',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `正在加入 ${workspaceName}…`,
    joiningWorkspaceBody: '请稍候。',

    eventLabels: {
      newLead: '新线索',
      unansweredDigest: '未解答摘要',
      crawlFailed: '抓取失败',
      weeklySummary: '每周总结',
    },
    unsubscribeLinkExpiredTitle: '这个链接已经过期',
    unsubscribeLinkExpiredBody: '在控制台的机器人设置中管理通知。',
    openDashboardCta: '打开控制台',
    unsubscribedTitle: '已取消订阅',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `你不会再收到${botName ?? '这个机器人'}的${eventLabel}邮件。你可以在机器人的设置中重新开启。`,

    notFoundEyebrow: '页面未找到',
    notFoundTitle: '这个地址什么都没有',
    notFoundBody: '链接可能已经过期,或者页面已经移动。',
    notFoundCta: '前往我的机器人',

    landingBotsErrorFallback: '加载你的机器人时出了点问题。',
    landingGoToBotsCta: '前往我的机器人 →',
  },
  onboarding: {
    skipForNow: '暂时跳过',
    invalidWebsiteUrl: '请输入有效的网站地址,例如 https://acme.vn',
    websiteUrlLabel: '网站地址',
    genericFallbackError: '出了点问题。',

    crawlDefaultHostname: '你的网站',
    noSourceToReplace: '目前还没有可替换的来源。',

    stepWebsite: '网站',
    stepCrawl: '抓取',
    stepTryIt: '试用',
    stepLook: '外观',
    stepGoLive: '上线',
    stepMobileReadout: ({ current, total, label }: { current: number; total: number; label: string }) =>
      `第 ${current}/${total} 步 · ${label}`,
    stepProgressAriaLabel: ({ current, total, label }: { current: number; total: number; label: string }) =>
      `引导进度:第 ${current} 步,共 ${total} 步,${label}`,
    stepTablistAriaLabel: '引导进度',

    websiteHeading: '添加你的网站',
    websiteSubtitle: 'Embent 会读取你的网站,搭建一个带引用来源的机器人——不会编造任何内容。大多数网站几分钟内就能就绪。',
    botNameLabel: '机器人名称',
    botNamePlaceholder: 'Acme 客服',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: '我们会自动发现你的页面。你之后还可以添加更多来源。',
    botNameRequired: '请为你的机器人输入一个名称。',
    websiteStarting: '启动中…',
    websiteSubmitCta: '开始阅读我的网站',

    crawlHeading: ({ hostname }: { hostname: string }) => `正在阅读 ${hostname}…`,
    crawlSubtitleReady: '全部完成——每个页面都已索引并可以回答问题。',
    crawlSubtitleSyncing: '不用在这里等——可以先试试你的机器人,同时它继续处理。',
    crawlStatusLines: {
      pending: ['排队中——即将开始…', '正在准备开始…'],
      crawling: ['正在查找网站页面…', '正在跟踪链接…', '正在阅读页面…'],
      indexing: ['正在构建搜索索引…', '正在处理已读取的内容…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `已索引 ${n} 页`,
    crawlNoSourceYet: '我们还没能为这个机器人找到来源。',
    crawlErrorLead: '最常见的原因是地址拼写有误——请在下方仔细核对后重试。',
    crawlReplaceUrlCta: '试试另一个地址',
    crawlReplaceUrlUpdating: '更新中…',
    crawlReplaceUrlHelper: '我们会移除失败的尝试,用这个新地址重新开始。',
    crawlRetryCta: '重试',
    crawlRetrying: '重试中…',
    crawlRetryHelper:
      '如果地址没错,只是网站暂时无法访问,直接重试即可。需要 JavaScript 才能显示内容的网站,可以在设置完成后前往"来源"页面开启相应设置来处理。',
    crawlCtaSyncing: '先试试你的机器人,同时它继续处理 →',
    crawlCtaReady: '下一步:试试你的机器人 →',

    tryItHeading: '随便问点什么',
    tryItSubtitleWithPages: ({ n }: { n: number }) => `它已经了解 ${n} 个页面——其余的还在学习中。每个回答都会显示来源。`,
    tryItSubtitleNoPages: '它还在阅读你的网站——不妨先试着问一个问题,回答只会越来越好。',
    tryItSamplePrompt: `试试问:"你们的退货政策是什么?"`,
    tryItPlaceholder: '问问物流、价格,或者任何问题…',
    tryItSending: '发送中…',
    tryItSendCta: '发送',
    tryItNextCta: '看起来不错——设置成我的风格',

    lookHeading: '让它匹配你的网站',
    lookSubtitle: '选一个品牌色——预览会随你的输入实时更新。之后还可以在"外观"里精细调整一切。',
    brandColorLabel: '品牌色',
    colorHexAriaLabel: '颜色的十六进制值',
    welcomeMessageLabel: '欢迎语',
    lookSaving: '保存中…',
    lookContinueCta: '继续',

    goLiveHeading: '一段代码,即可上线',
    goLivePasteBeforePrefix: '把它粘贴在结束的',
    goLivePasteBeforeSuffix: '标签之前——就这么简单,机器人会出现在页面右下角。',
    goLiveInstallGuidesLink: '平台安装指南',
    goLiveFirstAnswerNotice: '聊天插件回答第一位访客的那一刻,我们会发邮件通知你。',
    goLiveDashboardCta: '前往控制台',
    goLiveViewSourcesCta: '查看来源',
  },
  overview: {
    title: '概览',
    liveOnPrefix: '已上线:',
    answeringSince: ({ date }: { date: string }) => `${date} 起开始回答`,
    testInPlaygroundCta: '在 Playground 中测试',
    addSourcesCta: '添加来源',
    botDetailsLoadError: ({ message }: { message: string }) => `无法加载机器人详情——${message}`,

    messagesLabel: '消息 · 最近 14 天',
    conversationsLink: '对话 →',
    vsPreviousPeriod: '相比前 14 天',
    leadsLabel: '已获取线索',
    newLeadsCount: ({ n }: { n: number }) => `${n} 条新线索`,
    leadsThisWeek: ({ n }: { n: number }) => `本周 ${n} 条`,
    viewLeadsLink: '查看线索 →',
    unansweredLabel: '未解答的问题',
    fixThemLink: '去处理 →',

    knowledgeHeading: '知识库',
    pointAtSite: '把它指向你的网站',
    addSourceLink: '添加来源 →',
    addSourcesLink: '添加来源 →',
    pagesIndexed: '已索引页面',
    sourcesCountLabel: '来源',
    lastSyncedLabel: '最后同步',
    manageSourcesLink: '管理来源 →',
    resyncing: '重新同步中…',
    allSyncing: '全部同步中',
    resyncCta: '重新同步',
    resyncNothingToSync: '没有需要同步的内容——每个来源都已在同步中。',
    resyncGenericError: '无法开始重新同步——请重试。',

    latestConversationsHeading: '最新对话',
    allConversationsLink: '所有对话 →',
    noConversationsEmpty: '最近 14 天很安静——新对话会出现在这里。',
    noMessageFallback: '(无消息)',
    leadCapturedTag: '已获取线索',
    hasUnansweredTag: '有未解答',

    knowledgeAllReady: '全部就绪',
    knowledgeSyncing: '来源同步中',
    knowledgeNeedsAttention: '有来源需要处理',
    knowledgeRemoving: '正在移除来源',

    createdSubtitle: ({ time }: { time: string }) => `创建于 ${time}`,
    zeroStatMessagesHint: '从第一个问题开始计算',
    zeroStatLeadsHint: '姓名和邮箱,聊天过程中获取',
    zeroStatUnansweredHint: '目前没有需要处理的问题',
    heroReadyHeadline: '你的机器人已经可以回答问题了',
    heroIndexingHeadline: '仍在阅读你的网站',
    heroEmptyHeadline: '添加一个来源开始使用',
    heroEmptyBody: '它目前还没有任何可读取的内容。添加一个来源,几分钟内它就能开始回答问题。',
    heroIndexingErrorBody: '有一个来源需要处理——它不会自己完成。查看知识库卡片了解问题所在,解决后再放到你的网站上。',
    heroIndexingPrefix: '它还在阅读',
    heroIndexingNoSourceFallback: '你的来源',
    heroIndexingSuffix: '——几分钟后再来看看,然后就可以放到你的网站上了。',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `它读取了 ${n} 个页面`,
    heroReadyFromDomain: ',来自 ',
    heroReadyBodySuffix: ',已经足够了解这些内容来回答相关问题。只剩一步:把它放到你的网站上。',
    onlyPagesNudge: ({ n }: { n: number }) => `只有 ${n} 个页面?添加你的常见问题、价格或政策页面,让它有更多内容可以参考。`,
    addSourceCta: '添加来源',
    addSourceHelperText: '大约需要一分钟。等它有内容可以回答之后,嵌入代码就会出现在这里。',
    pasteBeforeBodyLabel: '在 </BODY> 之前粘贴这段代码',
    emailSnippetLink: '把这段代码发给我的开发者',
    emailSnippetSubject: ({ botName }: { botName: string }) => `把 ${botName} 嵌入我们的网站`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `请把这段代码粘贴在结束的 </body> 标签之前:\n\n${snippet}`,
    whatHappensNextHeading: '接下来会发生什么',
    stepVisitorAsksTitle: '有访客提问',
    stepVisitorAsksBody: '这个气泡出现在装了这段代码的每个页面右下角。不需要其他任何设置。',
    stepEmailAnswerTitle: '我们会把第一个回答发给你',
    stepEmailAnswerBody: '这样你能第一时间看到它究竟对你的客户说了什么。',
    stepUnansweredTitle: '它答不上来的内容会出现在这里',
    stepUnansweredBody: '它会拒绝回答,而不是编造答案。每一次拒绝都会变成一条等你处理的记录。',
    stepAddSourceTitle: '添加一个来源',
    stepAddSourceBody: '把它指向你的网站,或者上传文件、粘贴文本——它需要先有内容可读,才能开始回答。',
    stepAddSourceVisitorTitle: '有访客提问,你会收到回答的邮件通知',
    countersHeading: '计数从你的第一位访客开始',

    needsYouHeading: '待处理',
    allUnansweredLink: '所有未解答 →',
    needsYouEmpty: '没有需要处理的——你的机器人回答了所有被问到的问题。',
    needsYouIntro: '你的机器人说它不知道。回答一次,它就会永远记住。',
    askedSummary: ({ n, time }: { n: number; time: string }) => `被问过 ${n} 次 · 最近一次 ${time}`,
    writeAnswerCta: '写下答案',
    cancelCta: '取消',
    answerPlaceholder: '写下你会给这位访客的答案。机器人之后会一直使用它。',
    addingCta: '添加中…',
    addAsQaSourceCta: '添加为问答来源',
    saveAnswerError: '无法保存这个答案——请重试。',
  },
  sources: {
    pageTitle: '来源',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) => `${n} 个来源 · ${pageCount} 个页面 · 最后同步于 ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `无法加载这个机器人——${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: '文本',
    typeFile: '文件',

    colType: '类型',
    colUri: 'URI',
    colStatus: '状态',
    colRenderedMode: '渲染模式',
    colPages: '页面',
    colLastSynced: '最后同步',
    colActions: '操作',

    urlFieldLabel: '页面网址',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'Sitemap 网址',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: '标题',
    textTitlePlaceholder: '退款政策',
    textContentFieldLabel: '内容',
    textContentPlaceholder: '粘贴你想让机器人了解的文本…',
    fileFieldLabel: '文件',
    fileDropPrompt: '把文件拖到这里,或点击浏览',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) => `${extensions} · 最大 ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `这个文件超过了 ${maxSize} 的限制。`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : '这个文件'}不受支持——请使用 ${extensions}。`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `正在使用"${name}"——另外 ${count} 个文件已被拖入但忽略。`,
    fileDropNotAFile: '这不是一个文件——请从你的电脑拖入一个文件。',
    fileTitleFieldLabel: '标题(选填)',
    fileTitlePlaceholder: '默认使用文件名',

    addingCta: '添加中…',
    addSourceCta: '添加来源',

    emptyTitle: '把它指向你的网站',
    emptyDescription: '在上方添加一个网址、sitemap、文本或文件,我们就会开始读取。',

    reviewPagesLink: '查看页面',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) => `${count} 个页面内容几乎为空`,

    resyncCta: '重新同步',
    deleteCta: '删除',
    cancelCta: '取消',
    syncInProgressError: '同步正在进行——请稍后再试',

    deleteConfirmTitle: '删除这个来源?',
    deleteConfirmDescriptionPrefix: '将移除 ',
    deleteConfirmDescriptionSuffix: ' 以及它索引的所有内容。此操作无法撤销。',

    renderedModeExplainerAriaLabel: '什么是渲染模式?',
    renderedModeExplainerBody1Bold: '渲染模式',
    renderedModeExplainerBody1Rest: '会在真正的浏览器里先运行每个页面再读取,这样只有 JavaScript 运行后才会出现的内容也能被捕捉到。',
    renderedModeExplainerBody2:
      '普通网站保持关闭即可——速度更快、成本更低。当某个页面在爬虫看来是空的时(单页应用、控制台类页面、任何在页面打开后才加载文字的内容),再开启它。',
    renderedModeExplainerBody3: '权衡:每次抓取会更慢,并消耗更多页面额度。',

    renderedModeLabel: '渲染模式',
    modeRendered: '渲染模式',
    modeStandard: '标准模式',
    switchModeTitle: ({ mode }: { mode: string }) => `切换到${mode}?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `这会以${mode}重新抓取这个来源。返回内容有变化的页面会被重新索引,已经不存在的页面会被移除——新的抓取完成之前,当前索引仍然可用。`,
    switchModeCta: ({ mode }: { mode: string }) => `切换到${mode}`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `已抓取 ${n} 个页面。`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `已抓取 ${n} 个页面。${failedCount} 个页面未能加载,已跳过。`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `在我们自己的抓取容量耗尽前,抓取了 ${n} 个页面。这是我们这边的暂时问题——请等几分钟后重新运行抓取。`,
    truncatedDeadline: ({ n }: { n: number }) => `在这次抓取达到时间上限前,抓取了 ${n} 个页面。`,
    truncatedAttempts: ({ n }: { n: number }) => `在因页面无法加载而用完重试次数前,抓取了 ${n} 个页面。`,
    truncatedPageCap: ({ n }: { n: number }) => `抓取了 ${n} 个页面——这是你当前套餐页面上限所允许的最多数量。`,
    remainingAtLeast: ({ n }: { n: number }) => ` 至少还有 ${n} 个页面未被索引。`,
    remainingNone: ' 可能有部分页面未被索引。',
    crawlFailedGeneric: '这次抓取没有索引任何页面。',

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: '文本',
    typeFile: '文件',

    languagePrefix: '语言:',

    factType: '类型',
    factRenderedMode: '渲染模式',
    factLastSynced: '最后同步',

    resyncCta: '重新同步',
    deleteCta: '删除',

    latestCrawlHeading: '最近一次抓取',
    neverCrawledYet: '这个来源还没有被抓取过。',
    crawlRanInMode: ({ rendered }: { rendered: boolean }) => `这次抓取以${rendered ? '渲染' : '标准'}模式运行。`,

    failedUrlsHeading: '失败的网址',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) => ` ——共 ${total} 个,显示前 ${shown} 个`,
    offSiteRedirectsHeading: '站外跳转',
    offSiteRedirectsMoreCaveat: ' ——实际数量可能比这份列表显示的更多',
    nearEmptyCameBack: ({ n }: { n: number }) => `${n} 个页面返回的内容几乎为空。`,

    pagesIndexedHeading: '已索引页面',
    nearEmptyOnlyToggle: '仅显示近乎空白',

    noPagesIndexedYet: '目前还没有已索引的页面。',
    colUrl: 'URL',
    colCharacters: '字符数',
    colStatus: '状态',
    nearEmptyBadge: '近乎空白',

    indexStateIndexed: '已索引',
    indexStateIndexing: '索引中…',
    indexStateFailed: '失败',

    fileNoMarkdownExplainer:
      '以上传的文件本身存储——我们保留原始文件,而不是另存一份文本副本,所以这里没有内容可以预览。文件已被索引,机器人会据此作答。',
    nothingStoredForDocument: '这份文档没有存储任何内容。',

    storedContentHeading: '已存储的内容',
    nothingStoredYet: '目前还没有存储任何内容。',

    deleteDialogTitle: '删除这个来源?',
    deleteDialogDescriptionPrefix: '将移除 ',
    deleteDialogDescriptionSuffix: ' 以及它索引的所有内容。此操作无法撤销。',
    cancelCta: '取消',
  },
  conversations: {
    today: '今天',
    yesterday: '昨天',
    yourTesting: '你的测试',
    visitor: '访客',
    playgroundTag: 'Playground',
    noMessageFallback: '(无消息)',
    leadCapturedTag: '已获取线索',
    hasUnansweredTag: '有未解答',
    unansweredChipLabel: '未解答',
    loadingMore: '加载中…',
    loadMore: '加载更多',
    selectConversationPrompt: '选择一个对话查看完整记录。',
    backToConversations: '← 返回对话列表',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) => `开始于 ${time} · ${n} 条消息`,
    deleteConversationAriaLabel: '删除这个对话',
    leadSubmittedTag: '✓ 已提交线索',
    cancelCta: '取消',
    deletingCta: '删除中…',

    pageTitle: '对话',
    headerSubtitle: ({ n }: { n: number }) => `${n} 个对话 · 最新在前`,
    emptyTitle: '等待你的第一位访客',
    emptyDescription: '有人和你的机器人聊天后,对话就会出现在这里。',

    deleteAllCta: '删除所有对话',
    deleteDialogTitle: '删除这个对话?',
    deleteDialogDescription: '将永久删除这份记录及其中的消息。其中获取的线索会保留在"线索"页面。此操作无法撤销。',
    deleteCta: '删除对话',

    deleteAllDialogTitle: '删除所有对话?',
    deleteAllDescriptionPrefix: '将永久删除这个机器人的所有对话和消息。已获取的线索会保留在"线索"页面。此操作无法撤销。请输入 ',
    deleteAllDescriptionSuffix: ' 以确认。',
    confirmBotNameAriaLabel: '确认机器人名称',
  },
  leads: {
    filterNew: '新',
    filterContacted: '已联系',
    filterArchived: '已归档',
    filterAll: '全部',
    actionMarkNew: '标记为新',
    actionMarkContacted: '标记为已联系',
    actionArchive: '归档',
    statusNew: '新',
    statusContacted: '已联系',
    statusArchived: '已归档',

    filterByStatusAriaLabel: '按状态筛选',
    pageTitle: '线索',
    exportingCta: '导出中…',
    exportCsvCta: '导出 CSV',
    exportCsvError: '无法导出线索——请重试。',

    headerSubtitleAll: ({ n }: { n: number }) => `已获取 ${n} 条线索 · 最新在前`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} 条${filterLabel}线索 · 最新在前`,

    deleteAllCta: '删除所有线索',
    bulkActionsAriaLabel: '批量操作',
    selectedCount: ({ n }: { n: number }) => `已选择 ${n} 条`,
    bulkCapMessage: ({ max }: { max: number }) => `每次最多选择 ${max} 条线索`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `已更新 ${n} 条`,
    bulkUpdateError: '无法更新这些线索,请重试。',

    emptyTitle: '等待你的第一条线索',
    emptyDescription: '机器人聊天插件获取的线索会出现在这里。',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `没有${filterLabel}线索`,
    noFilterResultsDescription: '这个筛选条件下目前没有内容。',

    selectAllAriaLabel: '选择所有线索',
    colName: '姓名',
    colContact: '联系方式',
    colIntent: '意向',
    colStatus: '状态',
    colCaptured: '获取时间',
    colActions: '操作',

    noEmailFallback: '(无邮箱)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `选择线索 ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `删除线索 ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: '查看对话',
    loadingMoreCta: '加载中…',
    loadMoreCta: '加载更多',

    cancelCta: '取消',
    deletingCta: '删除中…',

    deleteDialogTitle: '删除这条线索?',
    deleteDescriptionPrefix: '将永久删除 ',
    deleteDescriptionSuffix: '。此操作无法撤销。',
    noContactFallback: '这条线索',
    deleteCta: '删除线索',

    deleteAllDialogTitle: '删除所有线索?',
    deleteAllDescriptionPrefix: '将永久删除这个机器人获取的所有线索。此操作无法撤销。请输入 ',
    deleteAllDescriptionSuffix: ' 以确认。',
    confirmBotNameAriaLabel: '确认机器人名称',
  },
  unanswered: {
    tabOpen: '待处理',
    tabDismissed: '已忽略',
    tabAnswered: '已回答',
    filterByStatusAriaLabel: '按状态筛选',

    pageTitle: '未解答',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) => `${n} 个问题${verb}`,
    subtitleVerbOpen: '等待回答',
    subtitleVerbDismissed: '已忽略',
    subtitleVerbAnswered: '已回答',

    openTabIntro: '你的机器人答不上来的问题,按内容分组。写一次答案——它就会变成一个问答来源,机器人从此永远记得。',

    emptyTitleOpen: '没有待处理的——你的机器人回答了所有被问到的问题。',
    emptyTitleDismissed: '没有已忽略的。',
    emptyTitleAnswered: '还没有已回答的问题。',

    askedOnce: '被问过 1 次',
    askedTimes: ({ n }: { n: number }) => `被问过 ${n} 次`,

    viewSourceCta: '查看来源',
    sourceRemoved: '来源已移除',
    viewChatCta: '查看对话',
    chatDeleted: '对话已删除',

    dismissCta: '忽略',
    reopenCta: '重新打开',
    writeAnswerCta: '写下答案',
    cancelCta: '取消',
    answerPlaceholder: '写下你会给这位访客的答案。机器人之后会一直使用它。',
    addingCta: '添加中…',
    addAsQaSourceCta: '添加为问答来源',
    answerAddedNotice: '已添加为问答来源——机器人从下一次对话起就会使用它。',
    saveAnswerError: '无法保存这个答案——请重试。',
  },
  appearance: {
    pageTitle: '外观',
    pageSubtitle: '聊天插件在你网站上的外观和问候语',

    colorLabel: '颜色',
    colorHexAriaLabel: '颜色的十六进制值',
    botNameLabel: '机器人名称',
    botNamePlaceholder: 'Acme 客服',
    welcomeMessageLabel: '欢迎语',
    welcomePlaceholder: '你好!今天有什么可以帮你的吗?',

    quickQuestionsLabel: '快捷问题',
    addQuestionCta: '添加问题',
    quickQuestionsHint: '可选——以按钮形式展示,访客还没打字时就能点击提问。',
    quickQuestionAriaLabel: ({ n }: { n: number }) => `快捷问题 ${n}`,
    quickQuestionPlaceholder: '你们的营业时间是?',
    removeCta: '移除',

    positionLabel: '启动按钮位置',
    positionBottomRight: '右下角',
    positionBottomLeft: '左下角',

    languageLabel: '语言',
    autoLanguageLabel: '匹配访客浏览器的语言',

    badgeLabel: '"Powered by Embent" 标识',
    badgeHint: '在聊天插件底部显示一个小的"Powered by Embent"链接。',
    badgeSwitchAriaLabel: '显示"Powered by Embent"标识',
    badgeLoadError: '无法加载——请刷新',
    freeBadgeNote: ({ plans }: { plans: string }) => `升级到 ${plans} 即可取消这个要求。`,

    saveCta: '保存',
    savingCta: '保存中…',
    savedLabel: '已保存',

    livePreviewHeading: '实时预览',
    previewCaption: '这里只渲染启动按钮和面板本身——周围的页面是故意留白的。',
    devHintPrefix: '如果什么都没出现,说明本地还没构建聊天插件包:运行',
    devHintIn: '在',
    devHintAndCopy: '并复制',
    devHintTo: '到',

    widgetPreviewTitle: '聊天插件预览',
  },
  botSettings: {
    pageTitle: '设置',
    pageSubtitle: '允许的域名、线索获取方式,以及机器人如何做自我介绍',

    leadCaptureOffLabel: '关闭',
    leadCaptureOffBlurb: '机器人不会向访客索要联系方式。',
    leadCaptureOnIntentLabel: '按意向触发',
    leadCaptureOnIntentBlurb: '只有在机器人答不上来,或访客想要人工跟进时,才会询问邮箱或电话。',
    leadCaptureAlwaysLabel: '总是',
    leadCaptureAlwaysBlurb: '机器人会在对话的第一轮交流之后,主动询问一次联系方式。',
    leadCapturePreChatLabel: '聊天前表单',
    leadCapturePreChatBlurb: '访客在开始聊天前先填写一份简短的联系表单(姓名、邮箱、选填电话)。机器人之后不会在对话中再次询问。',

    leadLevelSubtleLabel: '低调',
    leadLevelSubtleBlurb: '只有在访客主动要求联系或询问报价时,才会提出跟进。',
    leadLevelBalancedLabel: '均衡(推荐)',
    leadLevelBalancedBlurb: '当访客展现出真实的购买信号时提出一次——比如问怎么开始、想要演示,或提到自己的网站。',
    leadLevelProactiveLabel: '主动',
    leadLevelProactiveBlurb: '只要访客看起来在评估,就会主动提出;如果第一次没人回应,之后还会再问一次。',

    domainRequiredError: '必填——不要留空,请直接删除这一行',
    domainBareHostnameError: '只填写裸域名,例如"example.com"(不含协议或路径)',
    domainTooLongError: '太长了',

    aboutLabel: '关于这个机器人',
    aboutHint: '机器人始终知道的一段简短介绍——它是谁、服务谁——这样"你是谁?"之类的问题无需检索就能立刻回答。',
    aboutPlaceholder: '我们是 Acme 的客服助手,帮助客户设置和排查 Acme Widgets 的问题。',

    allowedDomainsLabel: '允许的域名',
    addDomainCta: '添加域名',
    allowedDomainsHint: '允许嵌入这个机器人聊天插件的网站。留空表示允许所有网站——建议在正式上线前锁定为你自己的域名。',
    domainAriaLabel: ({ n }: { n: number }) => `允许的域名 ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: '移除',

    leadCaptureLabel: '获取线索',
    leadLevelLabel: '主动程度?',

    saveCta: '保存',
    savingCta: '保存中…',
    savedLabel: '已保存',

    embedSnippetHeading: '嵌入代码',
    embedPastePrefix: '把它粘贴在结束的',
    embedPasteSuffix: '标签之前。',

    embedCopyCta: '复制',
    embedCopiedCard: '已复制!',
    embedCopiedCheck: '✓ 已复制',

    dangerZoneHeading: '危险操作',
    dangerZoneDescription: '将删除这个机器人及其来源、对话、线索和搜索索引。此操作无法撤销。',
    deleteBotCta: '删除这个机器人',
    deleteBotDialogTitle: ({ name }: { name: string }) => `删除"${name}"?`,
    dangerZoneDialogDescriptionPrefix: '将删除这个机器人及其来源、对话、线索和搜索索引。此操作无法撤销。请输入 ',
    dangerZoneDialogDescriptionSuffix: ' 以确认。',
    confirmBotNameAriaLabel: '确认机器人名称',
    cancelCta: '取消',
    deletingCta: '删除中…',
  },
  notifications: {
    heading: '通知',
    subheading: '邮件会发送到每个人的账户邮箱。Playground 中的活动不会触发任何邮件。',
    noMembers: '没有成员。',
    saveError: '无法保存这项设置,请重试。',

    roleOwner: '所有者',
    roleAdmin: '管理员',
    roleMember: '成员',
    cadenceOff: '关闭',
    cadenceDaily: '每天',
    cadenceWeekly: '每周',

    colMember: '成员',
    colNewLead: '新线索',
    colUnansweredDigest: '未解答摘要',
    colCrawlFailed: '抓取失败',
    colWeeklySummary: '每周总结',

    newLeadAriaLabel: ({ email }: { email: string }) => `发送新线索邮件到 ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `发送未解答摘要邮件到 ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `发送抓取失败邮件到 ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `发送每周总结邮件到 ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: '像访客一样和你的机器人聊天——这里的内容不会传到你的网站。',
    retrievalDebugLabel: '检索调试',
    hideDebugCta: '隐藏调试信息',
    newSessionCta: '新建会话',
    emptyStateText: '发送一条消息,测试这个机器人如何基于它的来源作答。',
    inputPlaceholder: '向这个机器人提问…',
    stopCta: '停止',
    sendCta: '发送',
    debugEmptyText: '提一个问题,看看它检索到了什么',
    genericError: '出了点问题。',
  },
  billing: {
    pageTitle: '账单',
    openingCta: '打开中…',
    manageBillingCta: '管理账单',

    memberGateTitle: '账单由工作区所有者或管理员管理。',
    memberGateDescription: '请联系所有者或管理员来更改这个工作区的套餐。',

    nowOnPlan: ({ name }: { name: string }) => `你现在使用的是 ${name} 套餐。`,
    finishingUp: '正在为你处理——如果套餐还没更新,请稍后刷新。',
    confirmingWithPolar: '已收到变更——正在与 Polar 确认…',

    internalWorkspaceLabel: '内部工作区',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / 月`,
    internalWorkspaceInline: '无限制,不涉及账单。',
    internalWorkspaceHeading: '内部工作区——无限制,不涉及账单。',
    internalWorkspaceBody:
      '所有套餐上限都会被绕过,这里没有可升级或结算的内容。用量仍会在上方记录以供查看,但永远不会推送给 Polar——这个工作区没有账单身份。',

    unlimitedWord: '无限制',
    unlimitedParens: '(无限制)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: '内部工作区 · 无限制 · 不涉及账单',
    capsSuffix: ({
      bots,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} 个机器人 · ${messages} 条消息 · ${pages} 页`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `永久免费 · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) => `${date} 续期 · ${capsSuffix}`,

    cancelsAtPeriodEnd: '将在周期结束时取消',
    planChangesPausedNotice: '套餐变更已暂停,直到当前周期结束。',
    planChangesPausedReason: '这个订阅设置为在周期结束时取消——请先恢复订阅,再更改套餐。',

    redirectingCta: '跳转中…',
    switchingCta: '切换中…',
    upgradeToCta: ({ name }: { name: string }) => `升级到 ${name}`,

    messagesThisMonthLabel: '本月消息数',
    pagesIndexedLabel: '已索引页面',
    messageNoun: '消息',
    pageNoun: '页面',
    messagesUnit: ({ n }: { n: string }) => `${n} 条消息`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) => `已达到${noun}上限的 ${pct}%`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) => ` ——${name} 会把它提升到 ${unit}。`,
    limitReached: ' 已达上限。',

    cancelSubscriptionCta: '取消订阅',
    cancelingCta: '取消中…',
    cancelDialogTitle: '取消订阅?',
    yourCurrentPlanFallback: '你当前的套餐',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `你的机器人会照常使用 ${planName},直到这个计费周期结束${
        renewsDate ? `(${renewsDate})` : ''
      }——之后工作区会降级为 Free。这不会立即发生。`,
    keepSubscriptionCta: '保留订阅',

    changePlanFallbackTitle: '更改套餐?',
    keepCurrentPlanCta: '保留当前套餐',
    confirmFallbackCta: '确认',

    checkoutErrorFallback: '发起结账时出了点问题。',
    portalErrorFallback: '打开账单管理时出了点问题。',
    cancelErrorFallback: '取消订阅时出了点问题。',

    currentPlanBadge: '当前套餐',
    botsUnit: ({ n }: { n: number }) => `${n} 个机器人`,
    seatsUnit: ({ n }: { n: number }) => `${n} 个席位`,
    messagesPerMonth: ({ n }: { n: string }) => `每月 ${n} 条消息`,
    pagesUnit: ({ n }: { n: string }) => `${n} 页`,
    badgeRequiredText: '"Powered by Embent" 标识',
    badgeOptionalText: '"Powered by Embent" 标识可选',
    upgradeCta: '升级',
    switchToCta: ({ name }: { name: string }) => `切换到 ${name}`,

    flatPricingNote:
      '统一定价,没有按坐席计算的复杂算法,也没有意外的超额费用——我们会在用量达到 80% 时提醒你,只有超过 110% 才会暂停新的回答,而且绝不会在回答中途掐断对话。',

    planChangeConfirm: {
      verbUpgrade: '升级',
      verbSwitch: '切换',
      title: ({ verb, name }: { verb: string; name: string }) => `${verb}到 ${name}?`,
      confirmNow: ({ verb }: { verb: string }) => `立即${verb}`,
      continueToPolarCta: '继续前往 Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `你将被带到 Polar 开通 ${name} 订阅(每月 $${price})——Polar 会在那里向你收费以完成开通。`,
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `这会立即切换你的套餐——你的机器人会马上开始使用 ${name} 的额度(每月 $${price})。Polar 今天不会向你收费:它会把按比例计算的差额加到你的下一张账单中。`,
    },
  },
  workspace: {
    pageTitle: '工作区设置',
    loadErrorMessage: '无法加载这个工作区。请刷新页面重试。',
    notAllowedTitle: '只有所有者和管理员能更改工作区设置。',
    notAllowedDescription: '请联系这个工作区的所有者或管理员进行更改。',

    nameLabel: '工作区名称',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} 个字符。会显示给这个工作区的所有人,以及邀请邮件中。`,

    uploadLogoCta: '上传 logo',
    removeLogoCta: '移除',
    logoHint: '正方形效果最好。支持 PNG、JPEG、WebP 或 GIF,最大 2 MB。',
    logoNotConfiguredError: '这个环境尚未配置 logo 上传功能。',
    uploadLogoErrorFallback: '无法上传 logo,请重试。',
    removeLogoErrorFallback: '无法移除 logo,请重试。',

    saveNameErrorFallback: '无法保存名称,请重试。',
    saveCta: '保存',
    savedLabel: '已保存',

    cropDialogTitle: '裁剪你的 logo',
    cropDialogDescription: '拖动调整位置,缩放以适配。会保存为 256×256 的正方形。',
    zoomLabel: '缩放',
    cropErrorFallback: '无法裁剪这张图片,请换一个文件试试。',
    cancelCta: '取消',
    useThisLogoCta: '使用这个 logo',
  },
  members: {
    pageTitle: '成员',
    membersOnlyTitle: '仅限成员访问',
    membersOnlyDescription: '请联系工作区所有者或管理员来管理成员。',
    unlimitedCapWord: '无限制',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `已用 ${used}/${cap} 个席位${pending > 0 ? `(${pending} 个邀请中)` : ''}`,
    inviteMemberCta: '邀请成员',

    roleOwner: '所有者',
    roleAdmin: '管理员',
    roleMember: '成员',
    roleOptionMember: '成员',
    roleOptionAdmin: '管理员',

    colName: '姓名',
    colEmail: '邮箱',
    colRole: '角色',
    leaveWorkspaceCta: '离开工作区',
    removeCta: '移除',

    pendingInvitesHeading: '待处理的邀请',
    noPendingInvites: '没有待处理的邀请。',
    inviteColEmail: '邮箱',
    inviteColRole: '角色',
    inviteColExpires: '过期时间',
    revokingCta: '撤销中…',
    revokeCta: '撤销',

    cancelCta: '取消',
    removingCta: '移除中…',
    leaveDialogTitle: '离开这个工作区?',
    removeDialogTitle: ({ name }: { name: string }) => `移除 ${name}?`,
    leaveDialogDescription: '你会立即失去访问权限——之后所有者或管理员可以重新邀请你。',
    removeDialogDescription: ({ name }: { name: string }) => `${name} 会立即失去这个工作区的访问权限。`,

    emailLabel: '邮箱',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: '角色',
    inviteErrorFallback: '发送这份邀请时出了点问题。',
    sendingInviteCta: '发送中…',
    sendInviteCta: '发送邀请',

    roleChangeErrorFallback: '无法更改这个角色。',
    removeErrorFallback: '无法移除这个成员。',
    revokeErrorFallback: '无法撤销这份邀请。',
  },
  account: {
    pageTitle: '账户',
    loadSignInMethodsError: '无法加载登录方式',
    emailPasswordLabel: '邮箱和密码',
    googleLabel: 'Google',

    savingCta: '保存中…',

    profileHeading: '个人资料',
    displayNameLabel: '显示名称',
    namePlaceholder: '你的名字',
    emailLabel: '邮箱',
    saveNameErrorFallback: '保存名称时出了点问题。',
    saveCta: '保存',
    savedLabel: '已保存',

    passwordHeading: '密码',
    passwordMismatchError: '新密码和确认密码不一致。',
    currentPasswordLabel: '当前密码',
    newPasswordLabel: '新密码',
    confirmNewPasswordLabel: '确认新密码',
    changePasswordErrorFallback: '修改密码时出了点问题。',
    changePasswordCta: '修改密码',
    passwordChangedLabel: '密码已修改',
  },
  adminImpersonate: {
    impersonateFailedFallback: '无法模拟这个用户。',
    lookupFailedFallback: '无法查找这个用户。',
    userNotFound: '未找到该用户账户。',
    noOwnerToView: '这个工作区没有所有者可以查看。',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `第 ${page}/${totalPages} 页 · 共 ${totalCount} 条`,
      prevCta: '上一页',
      nextCta: '下一页',
    },
    workspaces: {
      crawlSignalRenderedLabel: '依赖渲染模式抓取',
      crawlSignalSourcesAcrossBots: ({
        botCountLabel,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) => `个来源,分布在 ${botCountLabel} 个机器人中`,
      crawlSignalRenderedFootnote:
        '衡量整个机器人群体有多少依赖 Cloudflare Browser Rendering——这是我们目前在 Workers 免费套餐上获得的容量,而非付费套餐。',
      crawlSignalCapacityLabel: '触及我们自己的渲染容量上限(7天)',
      // `n` only drove English's singular/plural branch; Chinese has none, so the param is
      // unused but kept in the signature to match `en.ts`'s function shape exactly.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `个来源`,
      crawlSignalCapacityFootnote:
        '仅统计不同的来源,只针对我们自己的容量——绝不是某个客户的套餐上限。被杀死的进程或被回收的过期任务不会留下记录——这是一个下限。如果持续攀升,就该升级到 Workers 付费套餐(每月 $5)了。',

      emptyText: '目前还没有工作区。',
      internalBadge: '内部',
      cancelsAtPeriodEndHint: '将在周期结束时取消',
      colName: '名称',
      colOwner: '所有者',
      colPlan: '套餐',
      colBots: '机器人',
      colMembers: '成员',
      colMsgsPerMo: '消息/月',
      colPages: '页面',
      colCreated: '创建时间',
      colActions: '操作',
      noOwnerTitle: '这个工作区没有所有者',
      viewAsCta: '以此身份查看',
      switchingCta: '切换中…',
      viewAsErrorFallback: '失败',

      backToWorkspaces: '← 返回工作区列表',
      ownerLabel: '所有者:',
      bannedBadge: '已封禁',
      createdLabel: ({ date }: { date: string }) => `创建于 ${date}`,
      usageHeading: '用量',
      messagesThisMonthLabel: '本月消息数',
      pagesIndexedLabel: '已索引页面',
      botsLabel: '机器人',
      internalUsageNote: '内部工作区——不适用任何上限;用量仍会在上方记录。',
      botsHeading: '机器人',
      noBotsYet: '目前还没有机器人。',
      colBotName: '名称',
      colPublicId: '公开 ID',
      colStatus: '状态',
      colBotCreated: '创建时间',
      membersHeading: '成员',
      noMembersYet: '没有成员。',
      colEmail: '邮箱',
      colMemberName: '姓名',
      colRole: '角色',
      colJoined: '加入时间',
      memberRoleLabel: {
        owner: '所有者',
        admin: '管理员',
        member: '成员',
      },
      subscriptionHeading: '订阅',
      statusLabel: '状态:',
      periodEndLabel: '周期结束:',
      cancelsAtPeriodEndLabel: '将在周期结束时取消:',
      yesLabel: '是',
      noLabel: '否',
      polarSubscriptionIdLabel: 'Polar 订阅 ID:',
      manageInPolarLink: '在 Polar 中管理 ↗',
      noSubscriptionText: '没有订阅——这个工作区使用免费套餐。',
      dangerZoneHeading: '危险操作',
      internalToggleLabel: '内部工作区',
      internalToggleHint: '没有上限,不涉及账单——用量仍会记录,但不会推送给 Polar。',
      banOwnerLabel: '封禁所有者',
      banOwnerHint: '被封禁的用户无法登录。聊天插件/公开 API 不受影响。',
      unbanCta: '解除封禁',
      unbanningCta: '解除封禁中…',
      banCta: '封禁',
      demoteBeforeBanTitle: '封禁前请先把这位管理员降级为普通用户。',
      roleLabel: '角色',
      roleHint: '管理员可以访问所有内部管理页面。',
      cannotChangeOwnRoleTitle: '你不能更改自己的角色。',
      cancelCta: '取消',
      savingCta: '保存中…',
      confirmCta: '确认',
      markInternalDialogTitle: '将这个工作区标记为内部?',
      removeInternalDialogTitle: '取消内部状态?',
      markInternalDialogDescription:
        '这个工作区将绕过所有套餐上限(机器人、消息、页面),并且永远不会被计费。用量仍会为了可见性而记录,但每晚推送给 Polar 的用量会跳过它——因为没有可推送的账单身份。',
      removeInternalDialogDescription: '这个工作区将恢复正常的套餐限制和计费(如果它有有效订阅)。',
      banDialogTitle: ({ email }: { email: string | undefined }) => `封禁 ${email}?`,
      banDialogDescription: '这会立即让对方退出登录,并且在解除封禁之前无法再次登录。',
      banReasonLabel: '原因(选填)',
      banReasonPlaceholder: '为什么要封禁这个账户?',
      banningCta: '封禁中…',
      banUserCta: '封禁用户',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `将角色更改为 ${role}?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} 将获得所有内部管理页面的访问权限。`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) => `${email} 将失去内部管理页面的访问权限。`,
      updateWorkspaceErrorFallback: '无法更新这个工作区。',
      banUserErrorFallback: '无法封禁这个用户。',
      unbanUserErrorFallback: '无法解除对这个用户的封禁。',
      changeRoleErrorFallback: '无法更改这个用户的角色。',
    },
    bots: {
      colName: '名称',
      colPublicId: '公开 ID',
      colWorkspace: '工作区',
      colStatus: '状态',
      colSources: '来源',
      colLastCrawl: '最后抓取',
      colInstanceId: '实例 ID',
      noBotsYet: '目前还没有机器人。',

      backToBots: '← 返回机器人列表',
      workspaceLabel: '工作区:',
      instanceLabel: ({ id }: { id: string }) => `实例:${id}`,
      createdLabel: ({ date }: { date: string }) => `创建于 ${date}`,
      openOwnerViewCta: '打开所有者视图',
      openingCta: '打开中…',
      openOwnerViewErrorFallback: '无法打开所有者视图。',
      sourcesHeading: '来源',
      noSourcesYet: '目前还没有来源。',
      colUrl: 'URL',
      colType: '类型',
      colSourceStatus: '状态',
      colPages: '页面',
      colLastCrawled: '最后抓取',
      colError: '错误',
      recentJobsHeading: '最近任务',
      noJobsLinked: '目前还没有与这个机器人关联的任务。',
      colKind: '类型',
      colJobStatus: '状态',
      colAttempts: '尝试次数',
      colUpdated: '更新时间',
      colPayload: '数据',
      recentJobsFootnote:
        '最近任务是根据每个任务的数据尽力匹配的——如果一个任务的数据里不包含这个机器人的 ID 或它某个来源的 ID,即使间接影响到这个机器人,也不会显示在这里。',
    },
    jobs: {
      tabLabel: {
        all: '全部',
        running: '运行中',
        failed: '失败',
      },
      noFailedJobs: '没有失败的任务——队列很健康。',
      noJobsToShow: '没有可显示的任务。',
      colKind: '类型',
      colStatus: '状态',
      colPayload: '数据',
      colAttempts: '尝试次数',
      colCreated: '创建时间',
      colUpdated: '更新时间',
      colActions: '操作',
      retryCta: '重试',
      retryingCta: '重试中…',
      cancelCta: '取消',
      cancellingCta: '取消中…',
      cancelJobCta: '取消任务',
      keepJobCta: '保留任务',
      cancelDialogTitle: '取消这个任务?',
      retryJobErrorFallback: '无法重试这个任务。',
      cancelJobErrorFallback: '无法取消这个任务。',
      cancelDialogDescriptionListRow:
        '这会删除这条待处理的任务记录——它将永远不会运行。只有待处理的任务才能这样取消;正在运行的任务的处理程序可能已经在处理它了。',

      backToJobs: '← 返回任务列表',
      jobIdLabel: '任务 ID',
      attemptsLabel: '尝试次数',
      createdLabel: '创建时间',
      updatedLabel: '更新时间',
      runAfterLabel: '运行时间不早于',
      errorHeading: '错误',
      payloadHeading: '数据',
      cancelDialogDescriptionDetailPage: '这会删除这条待处理的任务记录——它将永远不会运行。',
    },
    posts: {
      description: '博客文章,包括草稿和已发布的。在这里编辑,发布后一分钟内就会在 embent.com 上生效。',
      newPostCta: '新建文章',
      emptyText: '目前还没有文章——创建第一篇吧。',
      colTitle: '标题',
      colStatus: '状态',
      colAuthor: '作者',
      colPublished: '发布时间',
      colUpdated: '更新时间',
      scheduledLabel: ({ date }: { date: string }) => `已计划 · ${date}`,
    },
    postEditor: {
      backToPosts: '← 文章列表',
      newPostTitle: '新建文章',
      editPostTitleFallback: '编辑文章',

      titleLabel: '标题',
      titlePlaceholder: 'Embent 如何回答支持问题',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: '摘要',
      excerptPlaceholder: '一两句话——显示在 /blog 上,并用作 meta 描述。',
      tagsLabel: '标签',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: '用逗号分隔。',
      coverUrlLabel: '封面图片网址',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      authorLabel: '作者',
      editAuthorCta: '编辑作者',
      newAuthorCta: '+ 新建作者',
      authorSelectPlaceholder: '选择一位作者',
      noAuthorsYet: '目前还没有作者——用上方的"+ 新建作者"添加一个。',

      visibilityScheduledText: ({ date }: { date: string }) => `将于 ${date} 上线`,
      visibilityLiveText: ({ date }: { date: string }) => `发布于 ${date}`,
      visibilityDraftText: '尚未发布',
      publishDateLabel: '发布日期',
      publishDateHintPrefix: '时间以 ',
      publishDateHintSuffix:
        ' 为准——即这个浏览器的时钟。留空表示发布后立即上线。设置一个未来时间,文章会在那之前保持隐藏——它会从博客、标签计数、sitemap 和 RSS feed 中消失,直到时间到达。不需要任何东西保持运行它就会自动出现。',

      bodyLabel: '正文(Markdown)',
      livePreviewLabel: '实时预览',

      missingFieldLabel: {
        title: '标题',
        slug: 'slug',
        excerpt: '摘要',
        body: '正文',
        author: '作者',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `保存前请添加:${fields}。`,

      savingCta: '保存中…',
      saveChangesCta: '保存更改',
      saveDraftCta: '保存草稿',
      unpublishingCta: '取消发布中…',
      schedulingCta: '计划中…',
      publishingCta: '发布中…',
      unpublishCta: '取消发布',
      scheduleCta: '计划发布',
      publishCta: '发布',
      savedFlash: '已保存',

      renderingPreview: '渲染中…',
      previewFailedPrefix: '预览加载失败:',

      uploadFailedFallback: '上传失败',
      bodyPlaceholder: '用 Markdown 撰写……粘贴或拖入图片即可内联上传。',
      uploadingText: '上传中…',
      imageUploadFailedPrefix: '图片上传失败:',

      deleteCta: '删除',
      deleteDialogTitle: ({ title }: { title: string }) => `删除"${title}"?`,
      deleteDialogDescriptionPrefix: '将永久删除这篇文章。此操作无法撤销。请输入',
      deleteDialogDescriptionSuffix: ' 以确认。',
      confirmPostTitleAriaLabel: '确认文章标题',
      cancelCta: '取消',
      deletingCta: '删除中…',
      deletePostCta: '删除文章',

      editAuthorDialogTitle: ({ name }: { name: string }) => `编辑 ${name}`,
      newAuthorDialogTitle: '新建作者',
      authorDialogDescription: '只使用真实署名——不得虚构身份。',
      authorNameLabel: '姓名',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: '身份',
      authorRolePlaceholder: '创始人',
      authorAvatarLabel: '头像网址',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: '简介',
      authorPersonaLabel: '写作人设(内部)',
      authorPersonaPlaceholder: '用简短、直接的句子写作。对炒作持怀疑态度。比起形容词更偏好具体的例子。',
      authorPersonaHint: '一份帮助按这个署名的语气撰写的说明——不会发布,也不会展示给读者。',
      authorSavingCta: '保存中…',
      authorAddingCta: '添加中…',
      saveAuthorCta: '保存作者',
      addAuthorCta: '添加作者',

      writingGuideHeading: '写作指南',
      writingGuideSubtitle: 'Markdown 支持、图片,以及一个 AI 撰写提示词',
      supportedMarkdownHeading: '支持的 Markdown',
      mdListHeadings: '标题,1 到 6 级',
      mdListLists: '无序和有序列表',
      mdListLinksPrefix: '链接——',
      mdListLinksSuffix: ',仅支持 http(s) 和 mailto',
      mdListImages: '正文任意位置的图片,包括文章中间——粘贴或拖入即可(见下方)',
      mdListCode: '行内代码和代码块,带语法高亮',
      mdListTables: '表格,GitHub 风格,支持列对齐',
      mdListBlockquotes: '引用块',
      strippedHeading: '会被移除的内容',
      strippedPrefix: '其他一切都会被移除。原始 HTML 标签,以及 ',
      strippedSuffix:
        ' 相关内容尤其绝不会保留——正文会先按一份固定的允许列表(标题、列表、链接、图片、代码、表格、引用块,加上它们所依赖的段落/强调标记)进行清理,再在任何地方渲染,包括这里的预览。',
      imagesHeading: '图片',
      imagesPrefix: '把图片粘贴或拖入正文——它会上传到 cdn.embent.com,并插入',
      imagesSuffix: ' 到光标所在的位置,方便它出现在段落中间。上方的封面图片是一个独立字段——它永远不会来自正文里拖入的内容。',
      copiedCta: '已复制!',
      copyAiPromptCta: '复制 AI 提示词',
      copiedToClipboard: '已复制到剪贴板。',
      clipboardUnavailable: '这个浏览器无法使用剪贴板——请手动选中并复制下方的提示词。',
      assemblesPrefix: '会整合统一的写作风格、',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) => `${authorName} 的人设`,
      assemblesNoAuthorClause: '一条"未选择作者"的说明',
      assemblesSuffix: '、上面的 Markdown 约定,以及一个建议的结构——把它粘贴到外部模型中,再把生成的草稿粘贴回正文。',

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `用 ${authorName} 的语气写作:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} 没有设置人设——请用平实、直接的语气写作。`,
      aiPromptAuthorFallbackLower: '这位作者',
      aiPromptAuthorFallbackUpper: '这位作者',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `你正在为 Embent(embent.com)撰写一篇博客文章,这是一个面向网站的 AI agent 平台。

写作风格:
- 直接、具体。没有炒作,没有营销套话。
- 短段落(2-4 句)。
- 绝不编造统计数据或客户名称。如果你没有真实的数字或真实的客户,就不要编一个。

作者语气:
${personaSection}

Markdown 约定(只有这些会被渲染——其他一切都会被移除):
- 标题(#、##、###)、无序和有序列表、引用块。
- 链接写作 [文字](网址)——仅支持 http(s) 和 mailto。
- 正文任意位置(包括文章中间)的图片,写作 ![替代文字](网址)。
- 行内代码和代码块。
- 表格(GitHub 风格)。
- 原始 HTML 以及 <script>/<style> 标签会被完全移除——只写纯 Markdown,不要写 HTML。

建议结构:
1. 标题
2. 一段引入
3. 4-7 个部分,每个部分都有一个 h2(##)标题
4. 一段简短的结尾

现在就把完整草稿写成 Markdown。`,
    },
    feedback: {
      filterLabel: {
        unhandled: '未处理',
        all: '全部',
      },
      typeLabel: {
        bug: '问题',
        feature: '功能建议',
        question: '疑问',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) => `Re: [Embent 反馈 ${id}] ${type}`,
      replyCta: '回复',
      colDate: '日期',
      colWorkspace: '工作区',
      colUser: '用户',
      colType: '类型',
      colMessage: '内容',
      colActions: '操作',
      emptyUnhandled: '没有未处理的反馈——收件箱清空了。',
      emptyAll: '目前还没有反馈。',
      updateFeedbackErrorFallback: '无法更新这条反馈。',
      reopeningCta: '重新打开中…',
      markingCta: '标记中…',
      reopenCta: '重新打开',
      markHandledCta: '标记为已处理',
      noBotFallback: '无机器人',
      noUaFallback: '无 UA',
    },
    playground: {
      demosAllTimeLabel: '历史演示总数',
      demos30dLabel: '演示数 · 30天',
      messagesAllTimeLabel: '历史消息总数',
      signedUpAllTimeLabel: '已注册',
      signedUp30dLabel: '已注册 · 30天',
      leadsAllTimeLabel: '已获取线索',
      leads30dLabel: '线索 · 30天',
      demosPerDayLabel: '日均演示数',
      messagesPerDayLabel: '日均消息数',
      demoUnit: (): string => '次演示',
      messageUnit: (): string => '条消息',
      noDemos30d: '最近 30 天没有演示。',
      statusBreakdownHeading: '状态分布(30天)',
      recentDomainsHeading: '最近的域名',
      noDemosYet: '目前还没有演示。',
      colHostname: '主机名',
      colStatus: '状态',
      colPages: '页面',
      colMessages: '消息',
      colAttempts: '尝试次数',
      colSignedUp: '已注册',
      colLeads: '线索',
      colCreated: '创建时间',
      signedUpAriaLabel: '已注册',
      notSignedUpAriaLabel: '未注册',
      noEmailFallback: '(无邮箱)',
    },
  },
} satisfies Catalog;
